import { formatMoney } from '../../lib/format.js'
import { Skeleton } from '../Skeleton.jsx'
import KpiTile from './KpiTile.jsx'

// GroupKpiBar — the 5-tile combined-metrics bar at the top of the
// All-Units dashboard (Ship 3).
//
// Tiles L→R: Today's Sales | Labor % | 14-day Waste | Open Catering |
// Items Below Par. Each tile is clickable into a by-unit drill-down
// (onTileClick(view)). A Refresh button sits at the bar's right edge.
//
// Loading shows a SKELETON row of 5 tile-shaped blocks — NOT
// KitchenLoader (that's AI-only) and NOT a blue spinner. This is a
// deterministic read; we never flash 0s then swap in real numbers.

const VIEW = {
  sales:    'sales-breakdown',
  labor:    'labor-breakdown',
  waste:    'waste-breakdown',
  catering: 'catering-breakdown',
  belowPar: 'reorder',
}

// Labor % color tiers: green <30, yellow 30-35, red >35; null → neutral.
function laborTone(pct) {
  if (pct == null) return 'neutral'
  if (pct < 30) return 'green'
  if (pct <= 35) return 'yellow'
  return 'red'
}

function salesSub(pct) {
  if (pct == null) return ''
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct}% vs last week same day`
}

function pluralItems(n) {
  return `${n} item${n === 1 ? '' : 's'}`
}

export default function GroupKpiBar({ phase, data, onTileClick, onRefresh }) {
  if (phase === 'loading') {
    return (
      <div className="mb-4" aria-label="Group KPIs loading">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col rounded-xl border border-black/10 bg-white px-3 py-2.5 shadow-sm"
              aria-hidden="true"
            >
              <Skeleton className="h-2.5 w-3/4" />
              <Skeleton className="mt-1.5 h-5 w-1/2" />
              <Skeleton className="mt-1.5 h-2.5 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="mb-4 rounded-xl border border-black/10 bg-white px-4 py-3 text-[12px] text-black/60 shadow-sm">
        Couldn't load your group KPIs right now.
      </div>
    )
  }

  const d = data || {}

  return (
    <div className="mb-4" aria-label="Group KPIs">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <KpiTile
          label="Today's Sales"
          value={formatMoney(d.sales_today)}
          sub={salesSub(d.sales_today_vs_prior_week_pct)}
          onClick={() => onTileClick(VIEW.sales)}
        />
        <KpiTile
          label="Labor %"
          value={d.labor_pct == null ? '—' : `${d.labor_pct}%`}
          tone={laborTone(d.labor_pct)}
          onClick={() => onTileClick(VIEW.labor)}
        />
        <KpiTile
          label="14-day Waste"
          value={formatMoney(d.waste_14d_cost)}
          sub={pluralItems(d.waste_14d_count || 0)}
          onClick={() => onTileClick(VIEW.waste)}
        />
        <KpiTile
          label="Open Catering"
          value={String(d.catering_open_count || 0)}
          sub={`${formatMoney(d.catering_pipeline_value)} pipeline`}
          onClick={() => onTileClick(VIEW.catering)}
        />
        <KpiTile
          label="Items Below Par"
          value={String(d.items_below_par_count || 0)}
          sub={`${d.items_critical_count || 0} critical`}
          onClick={() => onTileClick(VIEW.belowPar)}
        />
      </div>
      <div className="mt-1.5 flex justify-end">
        <button
          type="button"
          onClick={onRefresh}
          className="text-[11px] font-medium text-black/55 hover:text-blue-700"
        >
          Refresh
        </button>
      </div>
    </div>
  )
}
